// Permission bits, mirroring the backend `user_permissions` table.
//
// `users.permissions` is a bitmask. The backend decodes it into
// `user_details.permission_details` with `JOIN user_permissions p ON u.permissions & p.bit`
// (see `_modules/user.php` → getUserPermissions), so both fields describe the same thing:
// `permissions` is the raw mask, `permission_details` is the decoded [{ bit, name }] list.
//
// Gate on these helpers, never on the mask's total value. `permissions === 33` was the old CMS
// check, and because that is an equality test on the whole mask it revoked access as soon as the
// user gained ANY other bit: a CMS Admin who was also granted User Management Admin has
// 1 | 32 | 64 = 97, which is not 33, so they lost CMS access entirely.
//
// NOTE: product access for My Royal Genie / Royal Momentum / Royal Bridge is NOT part of this bitmask.
// Those are separate boolean columns on `user_details` (`is_mrg_user`, `is_royal_pro_user`,
// `is_royal_bridge_user`, `is_kam_user`), managed by the User Management page.
export const PERMISSION_BITS = {
    MOBILE_APP_USER: 1,
    CMS_ADMIN: 32,
    USER_MANAGEMENT_ADMIN: 64,
    CONTENT_MANAGEMENT_ADMIN: 128,
    CONTENT_MANAGEMENT_FULL: 256,
};

// The CMS gate requires CMS_ADMIN and nothing else. It historically required BOTH bits
// (1 + 32 = the old `33` literal), but MOBILE_APP_USER is incidental to CMS access, so it was
// dropped. Note this deliberately widens access versus the pairing: a user holding CMS_ADMIN
// without MOBILE_APP_USER (mask 32) now gets in, where before they did not.
export const CMS_REQUIRED_BITS = PERMISSION_BITS.CMS_ADMIN;

// Reads the bitmask out of a user payload. Accepts anything shaped `{ user_details: {...} }` —
// both `getUserDetails()` and a raw AUTH_USER response's `data.data` qualify.
const readBitmask = (user) => {
    const raw = Number(user?.user_details?.permissions);
    if (Number.isFinite(raw)) return raw;

    // Fall back to rebuilding the mask from the decoded list, in case a payload ever carries
    // `permission_details` without `permissions`.
    const details = user?.user_details?.permission_details;
    if (Array.isArray(details)) {
        return details.reduce((mask, item) => mask | (Number(item?.bit) || 0), 0);
    }

    return 0;
};

// True when every bit in `requiredBits` is set. Extra unrelated bits are ignored — that is the
// whole point versus the old equality check.
export const hasPermissionBits = (user, requiredBits) =>
    (readBitmask(user) & requiredBits) === requiredBits;

export const hasCmsAccess = (user) => hasPermissionBits(user, CMS_REQUIRED_BITS);

export const hasUserManagementAdmin = (user) =>
    hasPermissionBits(user, PERMISSION_BITS.USER_MANAGEMENT_ADMIN);

// Royal Momentum's Content Management section. A separate bit from both CMS_ADMIN and
// USER_MANAGEMENT_ADMIN — holding one says nothing about the others.
export const hasContentManagementAdmin = (user) =>
    hasPermissionBits(user, PERMISSION_BITS.CONTENT_MANAGEMENT_ADMIN);

// Content Management has two tiers, and the backend names them `sales_manager` and `full`:
//
// - `sales_manager` — bit 128 alone. Edits one agency's content (`scope: "agency"`).
// - `full` — bits 128 AND 256. Also saves at market level (`scope: "market"`), which writes
//   content for every agency in a market at once.
//
// The two are NOT independent flags: `full` is `sales_manager` plus 256, so 256 on its own
// grants nothing. Hence the pairing below rather than a bare 256 check.
//
// Why this matters on the client: the backend enforces the agency tier in log-only mode today
// (`Brief::ENFORCE_CONTENT_TIER = false` — a failing caller is recorded and still let through),
// but it enforces the market tier unconditionally. So an all-accounts save by a user without 256
// is rejected outright, and gating the entry point here is what stops them reaching the end of
// the flow before finding out.
export const CONTENT_MANAGEMENT_FULL_REQUIRED_BITS =
    PERMISSION_BITS.CONTENT_MANAGEMENT_ADMIN | PERMISSION_BITS.CONTENT_MANAGEMENT_FULL;

// The tier names the backend uses, and hands us pre-computed on
// `user_details.momentum_content_management_tier`.
export const CONTENT_MANAGEMENT_TIERS = {
    NONE: "none",
    SALES_MANAGER: "sales_manager",
    FULL: "full",
};

export const contentManagementTier = (user) =>
    user?.user_details?.momentum_content_management_tier;

// True when the user may save at market level — the `/content-management/all-accounts` branch.
//
// Prefer the backend's own answer over re-deriving it from the mask. The tier is resolved
// server-side from `users.is_royal_pro_user` AND `users.permissions`, so it is not a pure
// function of the bitmask: a user holding 128|256 but without `is_royal_pro_user` is not
// `full` to the backend, and a client that only counted bits would let them into a branch
// whose every save is then rejected.
//
// The bitmask path stays as a fallback for payloads predating the field (an `appUser` in
// localStorage from before the backend started sending it, which survives a deploy).
export const hasContentManagementFullAccess = (user) => {
    const tier = contentManagementTier(user);
    if (typeof tier === "string") return tier === CONTENT_MANAGEMENT_TIERS.FULL;
    return hasPermissionBits(user, CONTENT_MANAGEMENT_FULL_REQUIRED_BITS);
};
